/**
 * Adapter that wires the pure useScrollToMessage hook to Redux + RTK Query.
 * Provides fetchPage (paginate via agents API) and refetchPins (force-reload
 * the pinned list) without leaking those concerns into the pure hook.
 */

import { messageAgentsEndpoints } from '@saaga/shared/store/message-agents-endpoints.store'
import { messagePinEndpoints } from '@saaga/shared/store/message-pin-endpoints.store'
import { advancePageTo, syncMessagesFromApi } from '@saaga/shared/store/slices/chat.slice'
import { selectPageForConversation } from '@saaga/shared/store/slices/chat-selectors.store'
import { useAppDispatch, useAppSelector } from '@saaga/shared/store/store'
import type { RefObject } from 'react'
import { useCallback, useMemo } from 'react'
import type { VirtuosoHandle } from 'react-virtuoso'
import type { ChatItem } from '@/components/chat/chat-message.interface'
import type { UserLookupMap } from '@/hooks/use-org-members-lookup.hook'
import { transformMessageListToEntityList } from '@/utils/message-entity-transformer.util'
import { CHAT_MESSAGES_INCLUDE_NOTES } from './use-chat-message-sync.hook'
import {
  PINNED_MESSAGES_INCLUDE_NOTES,
  PINNED_MESSAGES_PAGE_SIZE,
} from './use-pinned-messages.hook'

import type { FetchPageArg, FreshPin, UseScrollToMessageReturn } from './use-scroll-to-message.hook'
import { useScrollToMessage } from './use-scroll-to-message.hook'

interface UseChatScrollToMessageParams {
  conversationId: string | null
  virtuosoRef: RefObject<VirtuosoHandle | null>
  firstItemIndex: number
  itemList: ChatItem[]
  userLookup: UserLookupMap
}

type AppDispatchFn = ReturnType<typeof useAppDispatch>

const buildMessageExistsLookup =
  (itemList: ChatItem[]) =>
  (id: string): boolean =>
    itemList.some((item) => item.id === id)

const performPageFetch = async (
  dispatch: AppDispatchFn,
  conversationId: string,
  userLookup: UserLookupMap,
  arg: FetchPageArg,
  signal: AbortSignal
): Promise<void> => {
  const promise = dispatch(
    messageAgentsEndpoints.endpoints.getAgentMessages.initiate(
      {
        conversationId,
        params: {
          page: arg.page,
          page_size: arg.pageSize,
          // Same include_notes as the sync hook so both paths share an RTK
          // Query cache entry and feed an identical stream of items back
          // into Redux — otherwise Virtuoso's indexes oscillate.
          include_notes: CHAT_MESSAGES_INCLUDE_NOTES,
        },
      },
      { forceRefetch: false }
    )
  )
  signal.addEventListener('abort', () => promise.abort())
  const result = await promise.unwrap()
  const entityList = transformMessageListToEntityList(result.items, userLookup)
  dispatch(
    syncMessagesFromApi({
      conversationId,
      messageList: entityList,
      hasMore: result.has_more,
      isFirstPage: false,
    })
  )
  // Single authoritative write to `paginationByConversation`. Uses
  // advancePageTo (max) instead of incrementPage to avoid double-counting
  // when useChatMessageSync's own load-more runs afterwards.
  dispatch(advancePageTo({ conversationId, page: arg.page }))
}

const refetchPinsViaRtk = async (
  dispatch: AppDispatchFn,
  conversationId: string
): Promise<FreshPin[]> => {
  const response = await dispatch(
    messagePinEndpoints.endpoints.listPins.initiate(
      {
        conversationId,
        pageSize: PINNED_MESSAGES_PAGE_SIZE,
        includeNotes: PINNED_MESSAGES_INCLUDE_NOTES,
      },
      { forceRefetch: true }
    )
  ).unwrap()
  return response.items.map((pin) => ({
    itemId: pin.item_id,
    pageNumber: pin.page_number,
    pageSize: pin.page_size,
  }))
}

export const useChatScrollToMessage = (
  params: UseChatScrollToMessageParams
): UseScrollToMessageReturn => {
  const dispatch = useAppDispatch()
  const currentPage = useAppSelector((state) =>
    params.conversationId ? selectPageForConversation(state, params.conversationId) : 1
  )

  const messageExistsInStore = useMemo(
    () => buildMessageExistsLookup(params.itemList),
    [params.itemList]
  )

  const fetchPage = useCallback(
    async (arg: FetchPageArg, ctx: { signal: AbortSignal }): Promise<void> => {
      if (!params.conversationId) return
      await performPageFetch(dispatch, params.conversationId, params.userLookup, arg, ctx.signal)
    },
    [dispatch, params.conversationId, params.userLookup]
  )

  const refetchPins = useCallback(async (): Promise<FreshPin[]> => {
    if (!params.conversationId) return []
    return refetchPinsViaRtk(dispatch, params.conversationId)
  }, [dispatch, params.conversationId])

  return useScrollToMessage({
    virtuosoRef: params.virtuosoRef,
    firstItemIndex: params.firstItemIndex,
    messageList: params.itemList,
    currentPage,
    messageExistsInStore,
    fetchPage,
    refetchPins,
  })
}
